import { useEffect, useMemo, useState } from "react";
import { MediaField } from "@/components/admin/MediaField";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ImportButtons } from "@/components/admin/import/ImportButtons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  listAdminMealPlans,
  createMealPlan,
  updateMealPlan,
  deleteMealPlan,
  reorderMealPlans,
  upsertSchema,
  type AdminMealPlan,
  type MealPlanInput,
} from "@/lib/admin/meal-plans.functions";

export const Route = createFileRoute("/_admin/admin/meal-plans")({
  component: MealPlansAdminPage,
});

type FormState = {
  category: string;
  title: string;
  description: string;
  image_url: string;
  alt_text: string;
  cutoff: string;
  is_active: boolean;
  is_demo: boolean;
  badge: string;
  button_text: string;
  button_link: string;
  enquiry_message: string;
  price_rupees: string;
  currency: "INR";
};

function emptyForm(): FormState {
  return {
    category: "",
    title: "",
    description: "",
    image_url: "",
    alt_text: "",
    cutoff: "",
    is_active: true,
    is_demo: false,
    badge: "",
    button_text: "",
    button_link: "",
    enquiry_message: "",
    price_rupees: "0.00",
    currency: "INR",
  };
}

function fromPlan(p: AdminMealPlan): FormState {
  return {
    category: p.category,
    title: p.title,
    description: p.description,
    image_url: p.image_url ?? "",
    alt_text: p.alt_text ?? "",
    cutoff: p.cutoff,
    is_active: p.is_active,
    is_demo: p.is_demo,
    badge: p.badge ?? "",
    button_text: p.button_text,
    button_link: p.button_link,
    enquiry_message: p.enquiry_message,
    price_rupees: (p.price_paise / 100).toFixed(2),
    currency: "INR",
  };
}

const REORDER_ERROR_MSG: Record<string, string> = {
  incomplete_id_list: "Reorder must include every meal plan. Reloading the list.",
  duplicate_ids: "Reorder contained duplicate rows.",
  unknown_id: "Reorder referenced an unknown meal plan.",
  empty_id_list: "Nothing to reorder.",
  forbidden: "You don't have permission to reorder meal plans.",
};

function MealPlansAdminPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listAdminMealPlans);
  const createFn = useServerFn(createMealPlan);
  const updateFn = useServerFn(updateMealPlan);
  const deleteFn = useServerFn(deleteMealPlan);
  const reorderFn = useServerFn(reorderMealPlans);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin", "meal-plans"],
    queryFn: () => listFn(),
    staleTime: 10_000,
  });

  const [editing, setEditing] = useState<AdminMealPlan | "new" | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState<AdminMealPlan | null>(null);

  useEffect(() => {
    if (editing === "new") setForm(emptyForm());
    else if (editing) setForm(fromPlan(editing));
  }, [editing]);

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin", "meal-plans"] }),
      queryClient.invalidateQueries({ queryKey: ["meal-plans", "public"] }),
    ]);

  const saveMutation = useMutation({
    mutationFn: async (payload: {
      id: string | null;
      patch: MealPlanInput;
    }) => {
      if (payload.id === null) {
        return createFn({ data: payload.patch as never });
      }
      return updateFn({ data: { id: payload.id, patch: payload.patch } as never });
    },
    onSuccess: async () => {
      toast.success("Meal plan saved");
      setEditing(null);
      setErrors({});
      await invalidate();
    },
    onError: (err) => {
      console.error(err);
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Couldn't save the meal plan: ${msg}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } as never }),
    onSuccess: async () => {
      toast.success("Meal plan deleted");
      setConfirmDelete(null);
      await invalidate();
    },
    onError: (err) => {
      console.error(err);
      toast.error("Couldn't delete the meal plan.");
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => reorderFn({ data: { ids } as never }),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: ["admin", "meal-plans"] });
      const previous = queryClient.getQueryData<AdminMealPlan[]>([
        "admin",
        "meal-plans",
      ]);
      if (previous) {
        const map = new Map(previous.map((p) => [p.id, p]));
        const reordered = ids
          .map((id, i) => {
            const row = map.get(id);
            return row ? { ...row, sort_order: (i + 1) * 10 } : null;
          })
          .filter((r): r is AdminMealPlan => r !== null);
        queryClient.setQueryData(["admin", "meal-plans"], reordered);
      }
      return { previous };
    },
    onError: async (err, _vars, ctx) => {
      console.error(err);
      if (ctx?.previous) {
        queryClient.setQueryData(["admin", "meal-plans"], ctx.previous);
      }
      const code = err instanceof Error ? err.message : "reorder_failed";
      toast.error(REORDER_ERROR_MSG[code] ?? "Couldn't reorder meal plans.");
      await queryClient.invalidateQueries({ queryKey: ["admin", "meal-plans"] });
    },
    onSuccess: async () => {
      await invalidate();
    },
  });

  const plans = useMemo(() => data ?? [], [data]);

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= plans.length) return;
    const ids = plans.map((p) => p.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(target, 0, moved);
    reorderMutation.mutate(ids);
  };

  const handleSave = () => {
    const normalizedPrice = form.price_rupees.trim();
    const priceRupees = Number(normalizedPrice);
    const hasValidPrice =
      /^\d+(\.\d{1,2})?$/.test(normalizedPrice) &&
      Number.isFinite(priceRupees) &&
      priceRupees >= 0 &&
      priceRupees <= 20_000_000;
    const parsed = upsertSchema.safeParse({
      ...form,
      price_paise: hasValidPrice ? Math.round(priceRupees * 100) : -1,
    });
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !fe[key]) fe[key] = issue.message;
      }
      if (!hasValidPrice) {
        fe.price_rupees = "Enter a valid rupee amount with up to 2 decimal places.";
      }
      setErrors(fe);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setErrors({});
    const id = editing && editing !== "new" ? editing.id : null;
    saveMutation.mutate({ id, patch: parsed.data });
  };

  if (isLoading) {
    return (
      <div>
        <AdminPageHeader title="Meal Plans" description="Manage the meal plans shown on the public site." />
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <AdminPageHeader title="Meal Plans" />
        <Alert variant="destructive">
          <AlertDescription>
            Couldn't load meal plans.{" "}
            <button className="underline" onClick={() => refetch()}>Retry</button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader
        title="Meal Plans"
        description="Create, edit, reorder and activate the meal plans shown on the public /meal-plans page."
        actions={<ImportButtons moduleId="meal-plans" />}
      />

      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {plans.length} meal plan{plans.length === 1 ? "" : "s"}
        </p>
        <Button onClick={() => setEditing("new")} className="gap-2">
          <Plus className="h-4 w-4" aria-hidden /> New meal plan
        </Button>
      </div>

      {plans.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No meal plans yet. Create the first one.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="p-3 w-24">Order</th>
                <th className="p-3">Title</th>
                <th className="p-3">Category</th>
                <th className="p-3">Status</th>
                <th className="p-3 w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p, i) => (
                <tr key={p.id} className="border-t border-border">
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => move(i, -1)}
                        disabled={i === 0 || reorderMutation.isPending}
                        aria-label="Move up"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => move(i, 1)}
                        disabled={i === plans.length - 1 || reorderMutation.isPending}
                        aria-label="Move down"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-foreground">{p.title}</div>
                    <div className="text-xs text-muted-foreground">{p.is_demo ? "Demo Dataset" : "Production Dataset"}</div>
                    {p.badge ? (
                      <div className="mt-1 text-xs text-muted-foreground">Badge: {p.badge}</div>
                    ) : null}
                  </td>
                  <td className="p-3 text-muted-foreground">{p.category}</td>
                  <td className="p-3">
                    {p.is_active ? (
                      <Badge variant="secondary">Active</Badge>
                    ) : (
                      <Badge variant="outline">Hidden</Badge>
                    )}
                    {p.is_demo ? (
                      <Badge variant="outline" className="ml-2">Demo</Badge>
                    ) : null}
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => setEditing(p)} aria-label="Edit">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setConfirmDelete(p)}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing === "new" ? "New meal plan" : "Edit meal plan"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <FieldRow label="Category *" error={errors.category}>
              <Input
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="Breakfast, Lunch, Snacks, Dinner…"
              />
            </FieldRow>
            <FieldRow label="Title *" error={errors.title}>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </FieldRow>
            <FieldRow label="Description" error={errors.description}>
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </FieldRow>
            <FieldRow label="Cutoff" error={errors.cutoff}>
              <Input
                value={form.cutoff}
                onChange={(e) => setForm({ ...form, cutoff: e.target.value })}
                placeholder="Order by 8:00 pm, previous day"
              />
            </FieldRow>
            <div className="grid gap-4 sm:grid-cols-2">
              <FieldRow
                label="Price (₹)"
                hint="Enter 0 for enquiry-only plans. Stored internally in paise."
                error={errors.price_rupees}
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  value={form.price_rupees}
                  onChange={(e) =>
                    setForm({ ...form, price_rupees: e.target.value })
                  }
                  placeholder="100.00"
                  aria-label="Price in rupees"
                />
              </FieldRow>
              <FieldRow
                label="Currency"
                hint="Razorpay payments currently support INR only."
              >
                <Input value={form.currency} disabled aria-label="Currency" />
              </FieldRow>
            </div>
            <FieldRow
              label="Image URL"
              hint="Optional. Any valid https:// URL. Leave blank to use the category default."
              error={errors.image_url}
            >
              <MediaField
                value={form.image_url}
                onChange={(next) => setForm({ ...form, image_url: next })}
                onAltSuggestion={(alt) =>
                  setForm((prev) => (prev.alt_text.trim() ? prev : { ...prev, alt_text: alt }))
                }
              />
            </FieldRow>
            <FieldRow
              label="Alt text"
              hint="Optional. Leave blank to auto-derive from title and category."
              error={errors.alt_text}
            >
              <Input
                value={form.alt_text}
                onChange={(e) => setForm({ ...form, alt_text: e.target.value })}
              />
            </FieldRow>
            <FieldRow label="Badge (optional)" error={errors.badge}>
              <Input
                value={form.badge}
                onChange={(e) => setForm({ ...form, badge: e.target.value })}
                placeholder="e.g. New, Popular"
              />
            </FieldRow>
            <FieldRow label="Button text" error={errors.button_text}>
              <Input
                value={form.button_text}
                onChange={(e) => setForm({ ...form, button_text: e.target.value })}
                placeholder="Enquire Now"
              />
            </FieldRow>
            <FieldRow
              label="Button link"
              hint="Optional. Internal path (/…) or https:// URL. Leave blank to open a WhatsApp enquiry."
              error={errors.button_link}
            >
              <Input
                value={form.button_link}
                onChange={(e) => setForm({ ...form, button_link: e.target.value })}
              />
            </FieldRow>
            <FieldRow
              label="Enquiry message"
              hint="Optional. Used when the button opens a WhatsApp enquiry."
              error={errors.enquiry_message}
            >
              <Textarea
                rows={2}
                value={form.enquiry_message}
                onChange={(e) => setForm({ ...form, enquiry_message: e.target.value })}
              />
            </FieldRow>
            <div className="flex items-center gap-3">
              <Switch
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
              <Label htmlFor="is_active">Active (visible on the public site)</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="is_demo"
                checked={form.is_demo}
                onCheckedChange={(v) => setForm({ ...form, is_demo: v })}
              />
              <Label htmlFor="is_demo">
                Demo content (shows the Demo Content label and excludes this plan from operational ordering)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this meal plan?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes “{confirmDelete?.title}” permanently. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FieldRow({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="mb-1 block">{label}</Label>
      {children}
      {hint && !error ? (
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      ) : null}
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}